import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { WeeklyTasks } from "@/components/WeeklyTasks";
import { TierBadge } from "@/components/TierBadge";
import { StarterKitPending } from "@/components/StarterKitPending";
import { Zap, Clock, Trophy, Sparkles } from "lucide-react";

const ELEVATED = ["admin", "dept_lead"];

export default function WeeklyTasksPage() {
  const { user } = useAuth();
  const canFilterByAsm = user && ELEVATED.includes(user.role);
  const [asmId, setAsmId] = useState("");
  const [asms, setAsms] = useState([]);
  const [wins, setWins] = useState(null);
  const [queue, setQueue] = useState(null);

  useEffect(() => {
    if (canFilterByAsm) api.get("/users").then((r) => setAsms(r.data.filter((u) => u.role === "asm")));
  }, [canFilterByAsm]);

  useEffect(() => {
    const params = asmId ? { asm_id: asmId } : {};
    api.get("/dashboard/fast-track-wins", { params: { days: 7, ...(asmId ? { asm_id: asmId } : {}) } })
      .then((r) => setWins(r.data))
      .catch(() => setWins({ wins: [] }));
    api.get("/dashboard", { params })
      .then((r) => setQueue(r.data.fast_track_queue || []))
      .catch(() => setQueue([]));
  }, [asmId]);

  return (
    <div>
      <PageHeader
        title="Weekly Tasks"
        subtitle="Nudges, Fast Track wins, and the 30-day bonus queue"
        actions={
          canFilterByAsm && (
            <select
              data-testid="asm-filter"
              value={asmId}
              onChange={(e) => setAsmId(e.target.value)}
              className="h-10 rounded-full border border-border bg-card px-4 text-sm font-medium"
            >
              <option value="">All ASMs</option>
              {asms.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          )
        }
      />

      <div className="p-6 md:p-10 space-y-8">
        {/* Auto-generated weekly nudges */}
        <WeeklyTasks asmId={asmId} />

        <StarterKitPending asmId={asmId} />

        {/* Fast Track Wins This Week */}
        {wins && wins.wins?.length > 0 && (
          <div className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 via-transparent to-transparent p-6" data-testid="fast-track-wins-tile">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-emerald-600" />
                <h3 className="text-lg font-semibold tracking-tight">Fast Track Wins This Week</h3>
                <span className="text-xs font-semibold uppercase tracking-widest bg-emerald-500/10 text-emerald-600 rounded-full px-2 py-0.5">{wins.wins.length}</span>
              </div>
              <span className="text-[11px] uppercase tracking-widest text-muted-foreground hidden md:inline">
                ₹{wins.reward_mrp?.toLocaleString("en-IN")} MRP bonus each · {wins.days}d window
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {wins.wins.map((w) => (
                <Link
                  to={`/affiliates/${w.affiliate_id}`}
                  key={w.affiliate_id}
                  data-testid={`fast-track-win-${w.affiliate_id}`}
                  className="rounded-xl border border-emerald-500/20 bg-card p-4 hover:bg-emerald-500/5 transition-colors group"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="shrink-0 h-8 w-8 rounded-full bg-emerald-500/15 flex items-center justify-center">
                        <Sparkles className="h-4 w-4 text-emerald-600" />
                      </div>
                      <div className="font-semibold truncate">{w.name}</div>
                    </div>
                    <TierBadge tier={w.tier} />
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{w.new_customers} new customers</span>
                    <span className="font-semibold text-emerald-600">₹{w.reward_mrp?.toLocaleString("en-IN")} bonus</span>
                  </div>
                  <div className="mt-1.5 text-[10px] text-muted-foreground">
                    Unlocked {new Date(w.at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Fast Track Queue */}
        <div className="rounded-2xl border border-border/60 bg-card p-6">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Zap className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-semibold tracking-tight">Fast Track Queue</h3>
            </div>
            <span className="text-[11px] uppercase tracking-widest text-muted-foreground">
              2 new customers in 30 days = ₹2,000 MRP bonus
            </span>
          </div>
          {queue === null ? (
            <div className="text-sm text-muted-foreground py-6">Loading Fast Track queue…</div>
          ) : queue.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8">No affiliates currently in Fast Track window.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {queue.map((a) => {
                const pct = Math.min(100, (a.new_customers_so_far / a.target) * 100);
                return (
                  <Link
                    to={`/affiliates/${a.affiliate_id}`}
                    key={a.affiliate_id}
                    data-testid={`fast-track-${a.affiliate_id}`}
                    className="rounded-xl border border-border/60 p-4 hover:bg-secondary/40 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-semibold">{a.name}</div>
                      <TierBadge tier={a.tier} />
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3">
                      <Clock className="h-3 w-3" />
                      {a.days_remaining} days left
                    </div>
                    <div className="h-2 rounded-full bg-secondary overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <div className="mt-2 text-xs font-medium">
                      {a.new_customers_so_far} / {a.target} new customers
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
